import React, { useState, useEffect, useRef, useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import { createItemInitialState } from '../../../../constants';
import SteelOrdersInfiniteSelect from '../../../../components/SteelOrdersInfiniteSelect';
import ApiService from '../../../../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../../../../contexts/FeedbackContext';

import { FormGroup, Input, Label } from '../../../../style';
import { IdentificationGrid } from './FinalProductIdentificationSection.style';

interface FinalProductIdentificationSectionProps {
  itemPath: ItemPath;
}

const FinalProductIdentificationSection: React.FC<FinalProductIdentificationSectionProps> = ({ itemPath }) => {
  const { register, control, setValue, watch, getValues } = useFormContext<OrderSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const [selectedScript, setSelectedScript] = useState<{ id: number; name: string }[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<{ id: number; name: string }[]>([]);

  const finalProductPath = `${itemPath}.finalProduct` as const;

  useEffect(() => {
    if (selectedScript.length > 0) {
      setValue(`${finalProductPath}.script`, selectedScript[0].name);
      setValue((`${itemPath}.scriptId`) as any, selectedScript[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedScript, setValue, finalProductPath]);

  const isTopLevelItem = !itemPath.includes('.subItems.');
  const prevScriptIdRef = useRef<number | null | 'uninit'>('uninit');

  useEffect(() => {
    if (prevScriptIdRef.current === 'uninit') {
      const existing = watch((`${itemPath}.scriptId`) as any) as any;
      prevScriptIdRef.current = existing ? Number(existing) : null;
    }

    const currentId = selectedScript[0]?.id ? Number(selectedScript[0].id) : null;
    if (!currentId || currentId === prevScriptIdRef.current) return;
    prevScriptIdRef.current = currentId;
    if (!isTopLevelItem) return;

    const pullScriptBreakdown = async () => {
      try {
        const quantity = Number(watch(`${finalProductPath}.quantidade` as any)) || 1;
        const unitIdRaw = watch((`${itemPath}.unitOfMeasurementId`) as any) as any;
        const params: { quantity: number; unitOfMeasurementId?: number } = { quantity };
        if (unitIdRaw) params.unitOfMeasurementId = Number(unitIdRaw);

        const res = await ApiService.steelOrders.getScriptBreakdown(currentId, params);
        const breakdown = res.data || {};
        const materials = Array.isArray(breakdown.materials) ? breakdown.materials : [];
        const services = Array.isArray(breakdown.services) ? breakdown.services : [];

        const materialSubItems = materials.map((m: any) => ({
          ...createItemInitialState('MATERIAL', false),
          materialId: m.materialId,
          quantity: (m.quantity !== undefined && m.quantity !== null) ? Number(m.quantity) : undefined,
          weight: (m.weight !== undefined && m.weight !== null) ? Number(m.weight) : undefined,
          unitOfMeasurementId: m.unitOfMeasurementId ?? undefined,
          fromScript: true,
          feedstock: {
            materiaPrima: m.materialName || '',
            quantidade: (m.quantity !== undefined && m.quantity !== null) ? Number(m.quantity) : undefined,
            anexos: [],
            observacoes: '',
            stockDescription: '',
          },
        }));

        const serviceSubItems = services.map((s: any) => {
          const price = (s.calculatedPrice !== undefined && s.calculatedPrice !== null)
            ? parseFloat(Number(s.calculatedPrice).toFixed(2))
            : undefined;
          const base = createItemInitialState('SERVICE', false);
          return {
            ...base,
            stageId: s.stageId,
            quantity,
            cost: (s.cost !== undefined && s.cost !== null) ? Number(s.cost) : undefined,
            markup: (s.markup !== undefined && s.markup !== null) ? Number(s.markup) : undefined,
            costUnitId: s.costUnitId ?? undefined,
            costUnitName: s.costUnitName || '',
            itemPrice: price,
            fromScript: true,
            stage: {
              servico: s.stageName || '',
              categoria: '',
              tipo: '',
              fornecedor: '',
              quantidade: quantity,
              anexos: [],
              observacoes: '',
            },
            priceDetails: { ...(base as any).priceDetails, itemSubtotal: price },
          };
        });

        const existingSubItems = (getValues((`${itemPath}.subItems`) as any) as any[]) || [];
        const manualSubItems = existingSubItems.filter((sub: any) => !sub?.fromScript);
        setValue(
          (`${itemPath}.subItems`) as any,
          [...manualSubItems, ...materialSubItems, ...serviceSubItems],
          { shouldDirty: true, shouldValidate: false }
        );
      } catch {
        setFeedback({ message: 'Não foi possível carregar o roteiro do script. Os subitens não foram preenchidos.', isOpen: true, statusOk: false });
      }
    };

    pullScriptBreakdown();
  }, [selectedScript, itemPath, finalProductPath, isTopLevelItem, watch, getValues, setValue, setFeedback]);

  useEffect(() => {
    const existingProductId = watch((`${itemPath}.productId`) as any) as number | undefined;
    const existingScriptId = watch((`${itemPath}.scriptId`) as any) as number | undefined;
    if (existingProductId && selectedProduct.length === 0) {
      setSelectedProduct([{ id: existingProductId, name: watch(`${finalProductPath}.produto`) || '' }]);
    }
    if (existingScriptId && selectedScript.length === 0) {
      setSelectedScript([{ id: existingScriptId, name: watch(`${finalProductPath}.script`) || '' }]);
    }
  }, [watch, itemPath, finalProductPath, selectedProduct.length, selectedScript.length]);

  useEffect(() => {
    if (selectedProduct.length > 0) {
      const prod = selectedProduct[0] as any;
      setValue(`${finalProductPath}.produto`, prod.name);
      setValue((`${itemPath}.productId`) as any, prod.id, { shouldDirty: true, shouldValidate: false });
      if ('productCode' in prod) {
        setValue(`${finalProductPath}.codigo`, prod.productCode ?? '', { shouldDirty: true, shouldValidate: false });
      }
      if ('ncmId' in prod) {
        setValue((`${itemPath}.ncmId`) as any, prod.ncmId ?? undefined, { shouldDirty: true, shouldValidate: false });
      }
      if ('norm' in prod) {
        setValue((`${itemPath}.productNorm`) as any, prod.norm ?? undefined, { shouldDirty: false, shouldValidate: false });
      }
      if ('formatId' in prod) {
        setValue((`${itemPath}.formatId`) as any, prod.formatId ?? undefined, { shouldDirty: true, shouldValidate: false });
      }
      if ('unitOfMeasurementId' in prod && prod.unitOfMeasurementId != null) {
        setValue((`${itemPath}.unitOfMeasurementId`) as any, prod.unitOfMeasurementId, { shouldDirty: true, shouldValidate: false });
        setValue((`${itemPath}.unitOfMeasurementName`) as any, prod.unitOfMeasurementName ?? '', { shouldDirty: true, shouldValidate: false });
        setValue((`${itemPath}.unitFromProduct`) as any, true, { shouldDirty: true, shouldValidate: false });
      } else {
        setValue((`${itemPath}.unitFromProduct`) as any, false, { shouldDirty: true, shouldValidate: false });
      }
      setSelectedScript([]);
      setValue(`${finalProductPath}.script`, '');
    } else {
      const existingProductId = watch((`${itemPath}.productId`) as any) as number | undefined;
      if (existingProductId === undefined || existingProductId === null) {
        setValue((`${itemPath}.productId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      }
      setSelectedScript([]);
      setValue(`${finalProductPath}.script`, '');
    }
  }, [selectedProduct, setValue, finalProductPath, itemPath, watch]);

  const watchedProductId = useWatch({ control, name: (`${itemPath}.productId` as any) });
  const watchedProductName = useWatch({ control, name: (`${finalProductPath}.produto` as any) });
  const watchedScriptId = useWatch({ control, name: (`${itemPath}.scriptId` as any) });
  const watchedScriptName = useWatch({ control, name: (`${finalProductPath}.script` as any) });

  useEffect(() => {
    if (watchedProductId && selectedProduct.length === 0) {
      setSelectedProduct([{ id: Number(watchedProductId), name: String(watchedProductName || '') }]);
    }
    if (watchedScriptId && selectedScript.length === 0) {
      setSelectedScript([{ id: Number(watchedScriptId), name: String(watchedScriptName || '') }]);
    }
  }, [watchedProductId, watchedProductName, watchedScriptId, watchedScriptName, selectedProduct.length, selectedScript.length]);

  useEffect(() => {
    const fetchScriptName = async () => {
      if (watchedScriptId && (!watchedScriptName || String(watchedScriptName).trim() === '')) {
        try {
          const res = await ApiService.steelOrders.getScripts({ id: Number(watchedScriptId) });
          const data = res.data;
          const scripts = Array.isArray(data?.scripts) ? data.scripts : Array.isArray(data) ? data : [];
          const first = scripts[0];
          const name = typeof first === 'string' ? first : (first?.name ?? '');
          if (name) {
            setValue((`${finalProductPath}.script`) as any, name, { shouldDirty: false, shouldValidate: false });
            setSelectedScript([{ id: Number(watchedScriptId), name }]);
          }
        } catch (e) {
          void e;
        }
      }
    };
    fetchScriptName();
  }, [watchedScriptId, watchedScriptName, setValue, finalProductPath]);

  return (
    <IdentificationGrid columns={3}>
      <FormGroup>
        <Label htmlFor={`${finalProductPath}.codigo`}>Código</Label>
        <Input
          id={`${finalProductPath}.codigo`}
          {...register(`${finalProductPath}.codigo`)}
          readOnly
          placeholder="—"
        />
      </FormGroup>
      <SteelOrdersInfiniteSelect
        label="Produto"
        items={selectedProduct}
        setItems={setSelectedProduct}
        endpoint="products"
        placeholder="Procure por nome ou código"
        onCleared={() => {
          setValue((`${itemPath}.productId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
          setValue((`${finalProductPath}.produto`) as any, '', { shouldDirty: true, shouldValidate: false });
          setValue((`${itemPath}.unitFromProduct`) as any, false, { shouldDirty: true, shouldValidate: false });
          setSelectedScript([]);
          setValue(`${finalProductPath}.script`, '');
        }}
      />
      <SteelOrdersInfiniteSelect
        label="Script"
        items={selectedScript}
        setItems={setSelectedScript}
        endpoint="scripts"
        placeholder="Selecione o script"
        additionalParams={selectedProduct[0]?.id ? { productId: selectedProduct[0].id } : {}}
        key={selectedProduct[0]?.id || 'product-scripts'}
        searchPlaceholder={selectedProduct[0]?.id ? 'Nenhum script encontrado' : 'Selecione um produto primeiro'}
        isDisabled={!selectedProduct[0]?.id}
        onCleared={() => {
          setValue((`${itemPath}.scriptId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
          setValue((`${finalProductPath}.script`) as any, '', { shouldDirty: true, shouldValidate: false });
        }}
      />
    </IdentificationGrid>
  );
};

export default FinalProductIdentificationSection;
